"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Multicall = void 0;
const ethers_1 = require("ethers");
const networks_1 = require("./networks");
const abis_1 = require("./abis");
const AbiMapper_1 = require("./AbiMapper");
// Multicall - A library for calling multiple contracts in aggregate
class Multicall {
    constructor(options) {
        // Extract the network or default to 1
        this.chainId = options && options.network ? options.network : 1;
        // If we have a network but not a provider, let's get the default provider for the given network
        this.provider =
            options && options.provider
                ? options.provider
                : ethers_1.ethers.getDefaultProvider(this.chainId);
        this.network = networks_1.networks[this.chainId.toString()];
        this.abi = abis_1.multicall3;
        this.multicall = this.network['multicall3'];
        if (options && options.address) {
            if (this.network['multicall'].toLowerCase() == options.address.toLowerCase()) {
                this.abi = abis_1.multicall1;
                this.multicall = this.network['multicall'];
            }
            else if (this.network['multicall2'].toLowerCase() ==
                options.address.toLowerCase()) {
                this.abi = abis_1.multicall2;
                this.multicall = this.network['multicall2'];
            }
        }
        this.interface = new ethers_1.ethers.utils.Interface(JSON.stringify(this.abi));
    }
    static encode(calls) {
        const callArray = !Array.isArray(calls) ? [calls] : calls;
        const encodedCalls = [];
        callArray.forEach((call) => {
            // Grab each call's abi
            const callInterface = new ethers_1.ethers.utils.Interface(call.abi);
            // Encode the call
            const encodedData = callInterface.encodeFunctionData(call.method, call.params);
            // Push to the list of encoded calls
            const newCall = Object.assign({ encodedData }, call);
            encodedCalls.push(newCall);
        });
        return encodedCalls;
    }
    // Constructs an ethers Contract
    static getContract(address, abi, provider) {
        // Create the contract interface using the provided abi
        const contractInterface = new ethers_1.ethers.utils.Interface(JSON.stringify(abi));
        // Construct the contract
        return new ethers_1.ethers.Contract(address, contractInterface, provider);
    }
    // Explodes an Aggregate Response into the Full Response
    static explode(callres, calls) {
        // Build the final result
        const exploded = {
            blockNumber: callres.blockNumber,
            blockHash: callres.blockHash,
            results: [],
        };
        // Map call responses to their original calls
        exploded.results = callres.returnData.map((res, index) => {
            return Object.assign({ returnData: res }, calls[index]);
        });
        // Finally, return the full response
        return exploded;
    }
    // Aggregate3 Call on Multicall3 Contract
    // Builds response from Multicall3 specific response format
    static aggregate3(calls, contract) {
        return __awaiter(this, void 0, void 0, function* () {
            // Construct Multicall3 Aggregate3 Calls
            const a3calls = calls.map((call) => {
                return {
                    target: call.address,
                    allowFailure: call.allowFailure,
                    callData: call.encodedData,
                };
            });
            const callres = yield contract.callStatic.aggregate3(a3calls);
            // Call Multicall3 aggregate3 method and get back the returnData[]
            const res = {
                returnData: callres,
            };
            return Multicall.explode(res, calls);
        });
    }
    // tryAggregate Call on Multicall2 or Multicall3 Contract
    static tryAggregate(calls, contract) {
        return __awaiter(this, void 0, void 0, function* () {
            // Map calls to the Multicall Call Struct format
            const mcalls = calls.map((call) => {
                return {
                    target: call.address,
                    callData: call.encodedData,
                };
            });
            const callres = yield contract.callStatic.tryBlockAndAggregate(
            // If any call doesn't allow failure, the whole tried aggregation should fail
            calls
                .map((call) => call.allowFailure)
                .reduce((acc, cur) => cur || acc, false), mcalls);
            const res = {
                blockNumber: callres[0],
                blockHash: callres[1],
                returnData: callres[2],
            };
            return Multicall.explode(res, calls);
        });
    }
    // tryAggregate Call on Multicall2 or Multicall3 Contract
    static aggregate(calls, contract) {
        return __awaiter(this, void 0, void 0, function* () {
            // Map calls to the Multicall Call Struct format
            const mcalls = calls.map((call) => {
                return {
                    target: call.address,
                    callData: call.encodedData,
                };
            });
            // Statically call on the multicall contract
            const res = yield contract.callStatic.aggregate(mcalls);
            // Translate the raw response into our response format
            const callres = {
                blockNumber: res[0],
                returnData: res[1],
            };
            return Multicall.explode(callres, calls);
        });
    }
    static execute(abi, multicall, provider, calls) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const contract = Multicall.getContract(multicall, abi, provider);
            // If the multicall contract has an aggregate3 method, use it
            if (((_a = contract.interface.functions['aggregate3((address,bool,bytes)[])']) === null || _a === void 0 ? void 0 : _a.name.length) > 0) {
                return yield Multicall.aggregate3(calls, contract);
            }
            else if (((_b = contract.interface.functions['tryAggregate(bool,(address,bytes)[])']) === null || _b === void 0 ? void 0 : _b.name.length) > 0) {
                // TODO: best design pattern to pass in a tryAggregate graceful param?
                // TODO: maybe in the top-level call methods, an object should be passed in with an option graceful param
                // TODO: and each call's allowFailure flag should also be an optional param
                return yield Multicall.tryAggregate(calls, contract);
            }
            else if (((_c = contract.interface.functions['aggregate((address,bytes)[])']) === null || _c === void 0 ? void 0 : _c.name.length) > 0) {
                return yield Multicall.aggregate(calls, contract);
            }
            else {
                throw new Error('Multicall contract does not have any supported aggregation method!');
            }
        });
    }
    static call(calls, options) {
        return __awaiter(this, void 0, void 0, function* () {
            // Encode the calls
            const encoded = Multicall.encode(calls);
            // Craft default configuration
            const map = (0, AbiMapper_1.abiMap)(options);
            const abi = map.abi;
            const address = map.address;
            const provider = options && options.provider
                ? options.provider
                : ethers_1.ethers.getDefaultProvider();
            // Execute and return the calls
            return yield Multicall.execute(abi, address, provider, encoded);
        });
    }
    call(calls, options) {
        return __awaiter(this, void 0, void 0, function* () {
            // Encode the calls
            const encoded = Multicall.encode(calls);
            // Craft custom configuration
            const map = options && (options.address || options.network)
                ? (0, AbiMapper_1.abiMap)(options)
                : {
                    found: false,
                    abi: this.abi,
                    address: this.multicall,
                    network: this.chainId,
                    interface: this.interface,
                };
            const abi = map.abi;
            const address = map.address;
            const provider = options && options.provider ? options.provider : this.provider;
            // Execute and return the calls
            return yield Multicall.execute(abi, address, provider, encoded);
        });
    }
}
exports.Multicall = Multicall;
//# sourceMappingURL=Multicall.js.map